import { ReactNode } from 'react';

import { CircleHelpIcon } from 'lucide-react';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface SimpleMetricProps {
  /** The title/label of the metric */
  title: string;
  /** Tooltip content explaining the metric */
  tooltip?: string;
  /** The formatted display value */
  value: string | number;
  /** The exact unformatted value (shown in tooltip when different from value) */
  exactValue?: string | number;
  /** Whether the metric is loading */
  isLoading?: boolean;
  /** Additional className for the container */
  className?: string;
  /** Custom skeleton width */
  skeletonWidth?: string;
  /** Children to render instead of value */
  children?: ReactNode;
}

/**
 * Reusable simple metric display component for analytics data.
 * Shows a title with optional tooltip and a formatted value.
 */
export const SimpleMetric = ({
  title,
  tooltip,
  value,
  exactValue,
  isLoading = false,
  className,
  skeletonWidth = 'w-16',
  children,
}: SimpleMetricProps) => {
  const showExactTooltip = exactValue !== undefined && exactValue !== value;

  return (
    <div className={cn('flex flex-col gap-1 pb-5', className)}>
      <div className="flex items-center">
        <span className="text-xs leading-5 font-semibold text-foreground">{title}</span>
        {tooltip && (
          <InfoTooltip content={tooltip} side="right">
            <CircleHelpIcon size={12} className="ml-1 text-foreground-muted" />
          </InfoTooltip>
        )}
      </div>
      {isLoading ? (
        <Skeleton className={`h-7 ${skeletonWidth}`} />
      ) : children ? (
        children
      ) : (
        <InfoTooltip
          content={String(exactValue)}
          open={showExactTooltip ? undefined : false}
          showArrow
        >
          <span className="text-xl leading-none font-semibold tracking-tight text-foreground">
            {value}
          </span>
        </InfoTooltip>
      )}
    </div>
  );
};
